import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { PrimeTimeCell } from './PrimeTimeCell';
import { PrimeTimesSidePanel } from './PrimeTimesSidePanel';
import { PrimeTimesMatrix, PrimeTimeMetric, ConversionMetric, PrimeTimeData, WEEKDAYS, HOUR_BUCKETS, CellDetail } from '../hooks/types/primeTimesTypes';
import { TooltipProvider } from '@/components/ui/tooltip';
import { formatBRL } from './utils/format';

type GridMetric = PrimeTimeMetric | ConversionMetric;

// Valor da célula para qualquer métrica (volume ou conversão)
function getCellValue(cell: PrimeTimeData | undefined, metric: GridMetric): number {
  if (!cell) return 0;
  switch (metric) {
    case 'plays': return cell.playsUnique || 0;
    case 'views': return cell.viewsUnique || 0;
    case 'conversions': return cell.conversions || 0;
    case 'revenue': return cell.revenue || 0;
    case 'conversionRate': return cell.conversionRate || 0;
    default: return 0;
  }
}

// Rótulo compacto exibido dentro da célula
function formatCellShort(value: number, metric: GridMetric): string {
  if (metric === 'conversionRate') return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
  if (metric === 'revenue') return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${Math.round(value)}`;
  return String(Math.round(value));
}

// Valor (mais completo) exibido no tooltip
function formatTooltipValue(value: number, metric: GridMetric): string {
  if (metric === 'conversionRate') return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
  if (metric === 'revenue') return formatBRL(value);
  return String(Math.round(value));
}

const METRIC_LABELS: Record<GridMetric, string> = {
  get plays() { return translateInterfaceText("plays únicos"); },
  get views() { return translateInterfaceText("visualizações únicas"); },
  get conversions() { return translateInterfaceText("conversões"); },
  get conversionRate() { return translateInterfaceText("de taxa de conversão"); },
  revenue: 'em receita'
};

interface PrimeTimesGridProps {
  data: PrimeTimesMatrix;
  metric: GridMetric;
}

export function PrimeTimesGrid({ data, metric }: PrimeTimesGridProps) {
  useLanguage();
  const [selectedCell, setSelectedCell] = useState<CellDetail | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Volume usa o max já calculado no transform; conversão calcula o pico a partir da matriz
  const maxValue = metric === 'plays'
    ? data.max.playsUnique
    : metric === 'views'
      ? data.max.viewsUnique
      : Math.max(0, ...data.matrix.map(cell => getCellValue(cell, metric)));
  const metricLabel = METRIC_LABELS[metric];

  const handleCellClick = (weekday: number, bucket: number) => {
    // Modal lateral desabilitado
  };

  return (
    <TooltipProvider delayDuration={100} skipDelayDuration={300}>
      <div className="w-full overflow-hidden">
        <div className="w-full">
          <div className="grid gap-1 md:gap-3" style={{ gridTemplateColumns: 'auto 1fr' }}>
            {/* Coluna 1: Labels de horário */}
            <div className="flex flex-col gap-[2px] md:gap-2">
              <div className="h-4 md:h-6 mb-1 md:mb-2" /> {/* Spacer para alinhar com header dos dias */}
              {HOUR_BUCKETS.map(bucket => (
                <div key={bucket.index} className="h-[28px] md:h-[44px] flex items-center justify-end pr-1 md:pr-2 text-[9px] md:text-[12px] font-normal text-[#6B7280]">
                  {`${String(bucket.fromHour).padStart(2, '0')}h`}
                </div>
              ))}
            </div>
            
            {/* Coluna 2: Grade 7×12 (dias × horários) */}
            <div className="w-full">
              {/* Header dos dias */}
              <div className="grid grid-cols-7 gap-[2px] md:gap-2 mb-1 md:mb-2">
                {WEEKDAYS.map(day => (
                  <div key={day.index} className="text-center text-[9px] md:text-[12px] font-medium text-[#0F172A]">
                    {day.label}
                  </div>
                ))}
              </div>
              
              {/* Células da grade */}
              {HOUR_BUCKETS.map(bucket => (
                <div key={bucket.index} className="grid grid-cols-7 gap-[2px] md:gap-2 mb-[2px] md:mb-2">
                  {WEEKDAYS.map(day => {
                    const cellData = data.matrix.find(
                      d => d.weekday === day.index && d.hourBucket === bucket.index
                    );
                    const value = getCellValue(cellData, metric);
                    const percent = maxValue > 0 ? value / maxValue : 0;

                    return (
                      <PrimeTimeCell
                        key={`${day.index}-${bucket.index}`}
                        value={value}
                        valueLabel={formatCellShort(value, metric)}
                        tooltipValue={formatTooltipValue(value, metric)}
                        percent={percent}
                        weekdayLabel={day.fullLabel}
                        bucketLabel={bucket.label}
                        metricLabel={metricLabel}
                        onClick={() => handleCellClick(day.index, bucket.index)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          
          {/* Legenda de intensidade */}
          <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-[#E5E7EB]">
            <span className="text-[11px] font-normal text-[#6B7280]">{translateInterfaceText("Menor")}</span>
            <div className="flex gap-1">
              <div className="w-6 h-3 rounded-sm bg-[#E9F0FF]" />
              <div className="w-6 h-3 rounded-sm bg-[#93C5FD]" />
              <div className="w-6 h-3 rounded-sm bg-[#7AB4FF]" />
              <div className="w-6 h-3 rounded-sm bg-[#5B9AFF]" />
              <div className="w-6 h-3 rounded-sm bg-[#3D7DFF]" />
            </div>
            <span className="text-[11px] font-normal text-[#6B7280]">{translateInterfaceText("Maior")}</span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
